/**
 * Validation utilities for AgentMeter SDK
 */

import { ValidationError } from '../types';
import type {
  CreateAppRequest,
  CreateMeterRequest,
  CreateEventRequest,
  UpdateAppRequest,
  UpdateMeterRequest,
} from '../types';

/**
 * Validates that a value is a non-empty string
 */
export function validateRequiredString(value: any, fieldName: string): void {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new ValidationError(`${fieldName} must be a non-empty string`);
  }
}

/**
 * Validates that a value is a positive number
 */
export function validatePositiveNumber(value: any, fieldName: string): void {
  if (typeof value !== 'number' || value <= 0 || !isFinite(value)) {
    throw new ValidationError(`${fieldName} must be a positive number`);
  }
}

/**
 * Validates an email format
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validates a URL format
 */
export function validateUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Validates ISO date string format
 */
export function validateISODate(date: string): boolean {
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
  return isoDateRegex.test(date) && !isNaN(Date.parse(date));
}

/**
 * Validates CreateAppRequest payload
 */
export function validateCreateAppRequest(request: CreateAppRequest): void {
  validateRequiredString(request.name, 'name');
  
  if (request.webhook_url && !validateUrl(request.webhook_url)) {
    throw new ValidationError('webhook_url must be a valid URL');
  }
}

/**
 * Validates UpdateAppRequest payload
 */
export function validateUpdateAppRequest(request: UpdateAppRequest): void {
  if (request.name !== undefined) {
    validateRequiredString(request.name, 'name');
  }
  
  if (request.webhook_url && !validateUrl(request.webhook_url)) {
    throw new ValidationError('webhook_url must be a valid URL');
  }
  
  if (request.status && !['active', 'inactive'].includes(request.status)) {
    throw new ValidationError('status must be either "active" or "inactive"');
  }
}

/**
 * Validates CreateMeterRequest payload
 */
export function validateCreateMeterRequest(request: CreateMeterRequest): void {
  validateRequiredString(request.name, 'name');
  validateRequiredString(request.event_name, 'event_name');
  validatePositiveNumber(request.price_per_unit, 'price_per_unit');
  
  const validAggregationMethods = ['SUM', 'COUNT', 'UNIQUE_COUNT', 'AVG', 'MAX', 'MIN'];
  if (!validAggregationMethods.includes(request.aggregation_method)) {
    throw new ValidationError(`aggregation_method must be one of: ${validAggregationMethods.join(', ')}`);
  }
  
  const validCurrencies = ['USD', 'EUR', 'GBP', 'CNY'];
  if (!validCurrencies.includes(request.currency)) {
    throw new ValidationError(`currency must be one of: ${validCurrencies.join(', ')}`);
  }
  
  const validBillingCycles = ['hourly', 'daily', 'weekly', 'monthly'];
  if (!validBillingCycles.includes(request.billing_cycle)) {
    throw new ValidationError(`billing_cycle must be one of: ${validBillingCycles.join(', ')}`);
  }
  
  // Validate aggregation_key is required for certain methods
  if (['SUM', 'AVG', 'MAX', 'MIN'].includes(request.aggregation_method) && !request.aggregation_key) {
    throw new ValidationError(`aggregation_key is required for aggregation_method: ${request.aggregation_method}`);
  }
}

/**
 * Validates UpdateMeterRequest payload
 */
export function validateUpdateMeterRequest(request: UpdateMeterRequest): void {
  if (request.name !== undefined) {
    validateRequiredString(request.name, 'name');
  }
  
  if (request.price_per_unit !== undefined) {
    validatePositiveNumber(request.price_per_unit, 'price_per_unit');
  }
  
  if (request.aggregation_method) {
    const validAggregationMethods = ['SUM', 'COUNT', 'UNIQUE_COUNT', 'AVG', 'MAX', 'MIN'];
    if (!validAggregationMethods.includes(request.aggregation_method)) {
      throw new ValidationError(`aggregation_method must be one of: ${validAggregationMethods.join(', ')}`);
    }
  }
  
  if (request.currency) {
    const validCurrencies = ['USD', 'EUR', 'GBP', 'CNY'];
    if (!validCurrencies.includes(request.currency)) {
      throw new ValidationError(`currency must be one of: ${validCurrencies.join(', ')}`);
    }
  }
  
  if (request.billing_cycle) {
    const validBillingCycles = ['hourly', 'daily', 'weekly', 'monthly'];
    if (!validBillingCycles.includes(request.billing_cycle)) {
      throw new ValidationError(`billing_cycle must be one of: ${validBillingCycles.join(', ')}`);
    }
  }
  
  if (request.status && !['active', 'inactive'].includes(request.status)) {
    throw new ValidationError('status must be either "active" or "inactive"');
  }
}

/**
 * Validates CreateEventRequest payload
 */
export function validateCreateEventRequest(request: CreateEventRequest): void {
  validateRequiredString(request.event_name, 'event_name');
  
  if (request.value !== undefined && (typeof request.value !== 'number' || !isFinite(request.value))) {
    throw new ValidationError('value must be a finite number');
  }
  
  if (request.timestamp && !validateISODate(request.timestamp)) {
    throw new ValidationError('timestamp must be a valid ISO date string');
  }
  
  if (request.properties && typeof request.properties !== 'object') {
    throw new ValidationError('properties must be an object');
  }
}

/**
 * Validates API key format
 */
export function validateApiKey(apiKey: string): void {
  if (typeof apiKey !== 'string' || apiKey.trim().length === 0) {
    throw new ValidationError('API key must be a non-empty string');
  }
  
  // Basic format validation - you might want to adjust this based on actual key format
  if (apiKey.length < 10) {
    throw new ValidationError('API key appears to be too short');
  }
}

/**
 * Validates pagination parameters
 */
export function validatePaginationParams(page?: number, limit?: number): void {
  if (page !== undefined && (!Number.isInteger(page) || page < 1)) {
    throw new ValidationError('page must be a positive integer');
  }
  
  if (limit !== undefined && (!Number.isInteger(limit) || limit < 1 || limit > 1000)) {
    throw new ValidationError('limit must be an integer between 1 and 1000');
  }
}